import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    MutationDefinition,
    QueryDefinition,
} from '@reduxjs/toolkit/dist/query/react';
import { UseMutation, UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import Loader from 'components/Loader';
import { FC, useCallback } from 'react';
import { HistoryDto, HistoryEntityDto, UpdateHistoryDto } from 'types/histories';
import { HistoryModalProps } from './HistoryModal';
import HistoryModalContent from './HistoryModalContent';

export type QueryAndMutationProps = {
    query: UseQuery<
        QueryDefinition<
            {
                id: string;
                type: string;
            },
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            string,
            HistoryDto[],
            'portalAPI'
        >
    >;
    updateMutation: UseMutation<
        MutationDefinition<
            HistoryEntityDto & UpdateHistoryDto,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            string,
            HistoryDto,
            'portalAPI'
        >
    >;
    deleteMutation: UseMutation<
        MutationDefinition<
            HistoryEntityDto,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            string,
            undefined,
            'portalAPI'
        >
    >;
};

type Props = QueryAndMutationProps & HistoryModalProps;

const DynamicHistoryModal: FC<Props> = ({
    titleId = 'history.modal.title',
    entityId,
    fieldType,
    fieldName,
    activity,
    onClose,
    query,
    updateMutation,
    deleteMutation,
}) => {
    const [updateRecord] = updateMutation();
    const [deleteRecord] = deleteMutation();
    const { data: historyData, isLoading: isLoadingHistoryData } = query({ id: entityId, type: fieldType });

    const handleUpdate = useCallback(
        async (record: HistoryEntityDto & UpdateHistoryDto) => {
            await updateRecord(record);
        },
        [updateRecord]
    );

    const handleDelete = useCallback(
        async (record: HistoryEntityDto) => {
            await deleteRecord(record);
        },
        [deleteRecord]
    );

    if (isLoadingHistoryData) {
        return <Loader />;
    }

    return (
        <HistoryModalContent
            activity={activity}
            fieldName={fieldName}
            fieldType={fieldType}
            onClose={onClose}
            titleId={titleId}
            historyData={historyData}
            handleUpdate={handleUpdate}
            handleDelete={handleDelete}
        />
    );
};

export default DynamicHistoryModal;
